import { MouseEvent, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getProductOfferingAttr } from "analytics/defaults";
import { fulfillmentQuoteAcceptedEvent } from "analytics/fulfillmentEvents";
import { productOfferEditEvent } from "analytics/productOfferEvents";
import Currency from "components/Currency/Currency";
import FormError from "components/FormError/FormError";
import {
  ProductOfferingColumn,
  ProductOfferingContainer,
  ProductOfferingDetails,
  ProductOfferingDetailsHeader,
  ProductOfferingDetailsItem,
  ProductOfferingHeader,
  ProductOfferingPremiumCalculation,
  ProductOfferingPremiumInfo,
  ProductOfferingSubheader,
  ProductOfferingValueProps,
} from "components/ProductOffering/ProductOffering";
import TLFAQ from "components/TLFAQ/TLFAQ";
import { ButtonFormItem, DynamicFormItems, IDynamicForm } from "config";
import { AppError } from "config/errors";
import { VerticalLayout } from "containers/DynamicForm/DynamicFormLayouts/VerticalLayout";
import { useButtonAction } from "containers/DynamicForm/useButtonAction";
import { Layout, useTranslation } from "modules";
import { useAppDispatch } from "store";
import { eventFired } from "store/actions/analytics";
import { setModal } from "store/actions/modal";
import { getWyshTermLifeOffering } from "store/selectors";
import { openChat } from "utils/customerSupport";
import { filterPrimaryButtons, QUOTE_REVIEW_ACCEPT_PRIMARY_BUTTON_KEY } from "utils/form";

import { defaultTo } from "lodash-es";

interface IProps {
  dynamicFormItems?: DynamicFormItems[];
  groups?: IDynamicForm["groups"];
  onClickEdit(offeringType: "termLifeOffering" | "adOffering"): void;
}

function CaseResultTerm({ onClickEdit, dynamicFormItems, groups }: IProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const productOffering = useSelector(getWyshTermLifeOffering);
  const productOfferingAttr = useSelector(getProductOfferingAttr);

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const premiumAmount = productOffering?.premiums?.find(premium => {
    return premium.billingFrequency === "monthly";
  })?.amount;

  const addPrimaryButton = dynamicFormItems?.find(
    item => item.key === QUOTE_REVIEW_ACCEPT_PRIMARY_BUTTON_KEY
  ) as ButtonFormItem;

  const actionCommands = useButtonAction(addPrimaryButton?.content.actions);

  const handleEdit = async () => {
    dispatch(productOfferEditEvent("term"));
    onClickEdit("termLifeOffering");
  };

  const handleChatClick = (event: MouseEvent<HTMLParagraphElement | HTMLHeadingElement>) => {
    event.stopPropagation();

    dispatch(
      eventFired({
        event: "fulfillment_quote_displayed_help",
        experienceLocation: "ONBOARDING",
        attribute: {
          faqname: "Chat with us",
        },
      })
    );

    openChat();
  };

  const handlePremiumCalculationModalOpen = () => dispatch(setModal("PremiumCalculationModal"));

  const handlePurchase = async () => {
    if (!productOffering) {
      return;
    }

    try {
      setIsLoading(true);

      await actionCommands();

      dispatch(fulfillmentQuoteAcceptedEvent());
    } catch (err) {
      const appErr = AppError.from(err);
      setError(defaultTo(appErr.fullMessage, appErr.code));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    dispatch(
      eventFired({
        event: "fulfillment_quote_displayed_pageview",
        attribute: {
          productOffering: productOfferingAttr,
        },
      })
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Layout as="TLProductOfferingLayout" backNav={{ hasBackNav: false }}>
      <ProductOfferingContainer className="tl-product-offering-layout__product-offering">
        <ProductOfferingColumn className="product-offering__column">
          <ProductOfferingHeader className="product-offering__header">
            {t("review_quotev2.term.header.title", "Your quote is ready for review")}
          </ProductOfferingHeader>
          <ProductOfferingSubheader className="product-offering__subheader">
            {t(
              "review_quotev2.term.header.subtitle",
              "Congratulations! Your application for Term Life Insurance has been approved."
            )}
          </ProductOfferingSubheader>

          <VerticalLayout groups={groups} items={filterPrimaryButtons(dynamicFormItems)} />

          <ProductOfferingPremiumInfo
            className="product-offering__premium-info"
            cta={{
              onClick: handlePurchase,
              text:
                addPrimaryButton?.content.text ||
                t("review_quotev2.term.offer.cta", "PURCHASE TERM LIFE"),
            }}
            expiresAt={productOffering?.expiresAt}
            isLoading={isLoading}
            premiumAmount={premiumAmount}
          />

          {!!error && <FormError error={error} />}

          <ProductOfferingDetails className="product-offering__details">
            <ProductOfferingDetailsHeader onEditClick={handleEdit} />

            <ProductOfferingDetailsItem
              description={t(
                "review_quotev2.term.offer.info.coverage.description",
                "Total coverage"
              )}
              value={
                productOffering?.amount ? (
                  <Currency amount={productOffering?.amount} fixedPoints={0} />
                ) : (
                  t("dollar.amount", "$%<amount>s", { amount: "-----" })
                )
              }
            />
            <ProductOfferingDetailsItem
              description={t("review_quotev2.term.offer.info.term.description", "Until you're")}
              value={t("review_quotev2.term.offer.info.term.value", "%<number>s y/o", {
                number: productOffering?.insuredsAgeAtTerm || "--",
              })}
            />
          </ProductOfferingDetails>

          <ProductOfferingPremiumCalculation
            className="product-offering__premium-calculation"
            onClick={handlePremiumCalculationModalOpen}
          />
        </ProductOfferingColumn>

        <ProductOfferingColumn className="product-offering__column product-offering__column--sidebar">
          <ProductOfferingValueProps
            className="product-offering__value-props"
            onChatClick={handleChatClick}
          />
        </ProductOfferingColumn>
      </ProductOfferingContainer>

      <TLFAQ />
    </Layout>
  );
}

export default CaseResultTerm;
